import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import io from 'socket.io-client'
import { listEntriesByClient } from '../services/entries'
import { brand } from '../types/brand'
import { entries } from '../types/entries'
import { Pagination } from '../types/types'
import { useAudio } from './AudioProvider'
import { useAuth } from './AuthProvider'
import { useLoading } from './LoadingProvider'

import { listBrandsAll } from '../services/brand'
import { convertEntriesAttributes } from '../utils/utils'
type CheckInContextType = {
  data: Pagination<entries>
  search: string
  setSearch: Dispatch<SetStateAction<string>>
  brands: brand[]
  getDataCheckIn: (searchValue: string, page: number) => void
  lastEntry: entries | null
  setLastEntry: Dispatch<SetStateAction<entries | null>>
}

const CheckInContext = createContext<CheckInContextType | undefined>(undefined)

type CheckInProviderProps = {
  children: ReactNode
}

const CheckInProvider: React.FC<CheckInProviderProps> = ({ children }) => {
  const [search, setSearch] = useState('')
  const { startLoading, stopLoading } = useLoading()
  const { token } = useAuth()
  const { user } = useAuth()
  const { play } = useAudio()
  const [filteredBySearch, setFilteredBySearch] = useState(false)
  const [brands, setBrands] = useState<brand[]>([])

  // Refs para evitar stale closures dentro del callback del socket
  // (el useEffect del socket solo se monta una vez)
  const searchRef = useRef(search)
  const currentPageRef = useRef(1)

  // const [destine, setDestine] = useState<string[]>([])
  const [lastEntry, setLastEntry] = useState<entries | null>(null)
  const [data, setData] = useState<Pagination<entries>>({
    currentPage: 1,
    hasNextPage: false,
    hasPrevPage: false,
    total: 1,
    totalPages: 1,
    data: []
  })

  useEffect(() => {
    searchRef.current = search
  }, [search])

  useEffect(() => {
    currentPageRef.current = data.currentPage
  }, [data.currentPage])
  const getDataCheckIn = async (searchValue: string, page: number, noLoading?: boolean) => {
    try {
      !noLoading && startLoading()
      const entries = await listEntriesByClient({
        id: user?.clientId as string,
        params: { page, search: searchValue },
        token
      })
      setData({
        ...entries,
        data: entries.data.map(convertEntriesAttributes)
      })
    } catch (error) {
      console.error(error)
    } finally {
      stopLoading()
    }
  }
  const getData = async () => {
    try {
      const brands = await listBrandsAll(token)
      setBrands(brands)
    } catch (error) {
      console.error(error)
    }
  }
  useEffect(() => {
    if (search.length >= 3) {
      setFilteredBySearch(true)
      getDataCheckIn(search, 1)
    } else if (filteredBySearch) {
      setFilteredBySearch(false)
      getDataCheckIn('', 1)
    }
  }, [search])

  useEffect(() => {
    getDataCheckIn('', 1)
  }, [])

  useEffect(() => {
    const socket = io(process.env.REACT_APP_API_URL_BACKEND as string)

    socket.on(`newEntry-${user?.clientId}`, (entry: { newEntry: entries }) => {
      const newEntry = entry.newEntry
      if (searchRef.current !== '' || currentPageRef.current !== 1) return
      getDataCheckIn('', 1, true)

      const dataOkid = newEntry.okid ? parseInt(newEntry?.okid) : 0
      const RF = newEntry.trm ? parseInt(newEntry?.trm) : 0
      const MIN_PERCENTAGE_OK = parseInt(process.env.REACT_APP_API_MIN_PERCENTAGE_OK!)
      const MIN_PERCENTAGE_RF = parseInt(process.env.REACT_APP_API_MIN_PERCENTAGE_RF!)

      const isGoodEntry =
        newEntry.DNI &&
        !newEntry.FECHA_DEFUNCION &&
        dataOkid >= MIN_PERCENTAGE_OK &&
        RF >= MIN_PERCENTAGE_RF

      play(isGoodEntry ? 'CODIGO_10_10' : 'CODIGO_20_20')
    })
    socket.on(`updateEntry-${user?.clientId}`, () => {
      if (searchRef.current !== '' || currentPageRef.current !== 1) return
      getDataCheckIn('', 1, true)
    })

    return () => {
      socket.disconnect()
    }
  }, [user?.clientId])

  useEffect(() => {
    if (!brands.length /*  || !destine.length */) {
      getData()
    }
  }, [])
  useEffect(() => {
    setLastEntry(data.data[0])
  }, [data.data])

  return (
    <CheckInContext.Provider
      value={{
        setLastEntry,
        lastEntry,
        data,
        brands,
        search,
        setSearch,
        getDataCheckIn
      }}
    >
      {children}
    </CheckInContext.Provider>
  )
}

const useCheckIn = () => {
  const context = useContext(CheckInContext)
  if (!context) {
    throw new Error('useCheckIn must be used within CheckInProvider')
  }
  return context
}

export { CheckInProvider, useCheckIn }
